<template>
  <div class="tip-form-wrapper">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status"></div>
      <p class="mt-2 text-muted">Načítám data...</p>
    </div>

    <form v-else @submit.prevent="submitTips" class="animate-fade-in">
      <div v-if="matches.length">
        <div
          v-for="match in matches"
          :key="match.id"
          class="match-card"
          :class="{ 'match-locked': isMatchStarted(match.matchDate) }"
        >
          <div class="match-header">
            <span class="date-badge">
              <svg
                class="icon-xs me-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {{ formatDate(match.matchDate) }}
            </span>

            <span
              v-if="isMatchStarted(match.matchDate)"
              class="status-badge locked"
            >
              <svg
                class="icon-xs me-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              ZÁPAS JIŽ ZAČAL
            </span>
            <span v-else class="status-badge active">
              <svg
                class="icon-xs me-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                ></path>
                <path
                  d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                ></path>
              </svg>
              OTEVŘENO
            </span>
          </div>

          <div class="match-body">
            <div class="team-block">
              <img :src="match.homeLogo" alt="Domácí" class="team-logo" />
              <span class="team-name">{{ match.homeTeam }}</span>
            </div>

            <div class="score-block">
              <input
                type="number"
                class="score-input"
                v-model.number="tips[match.id].homeScore"
                min="0"
                required
                :disabled="isMatchStarted(match.matchDate)"
              />
              <span class="vs-divider">:</span>
              <input
                type="number"
                class="score-input"
                v-model.number="tips[match.id].awayScore"
                min="0"
                required
                :disabled="isMatchStarted(match.matchDate)"
              />
            </div>

            <div class="team-block">
              <img :src="match.awayLogo" alt="Hosté" class="team-logo" />
              <span class="team-name">{{ match.awayTeam }}</span>
            </div>
          </div>
        </div>

        <div v-if="question" class="bonus-card mt-5 fade-in">
          <div class="bonus-header">
            <h3 class="bonus-title d-flex align-items-center">
              <svg
                class="icon-md me-2 bonus-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              DOPLŇUJÍCÍ OTÁZKA
            </h3>
            <span class="deadline-info d-flex align-items-center">
              <svg
                class="icon-xs me-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              Uzávěrka: {{ formatDate(deadline) }}
            </span>
          </div>

          <p class="question-text">{{ question }}</p>

          <div v-if="!isDeadlinePassed" class="radio-group mt-4">
            <label
              class="custom-radio"
              :class="{ selected: questionAnswer === 'Ano' }"
            >
              <input type="radio" value="Ano" v-model="questionAnswer" />
              <span>ANO</span>
            </label>
            <label
              class="custom-radio"
              :class="{ selected: questionAnswer === 'Ne' }"
            >
              <input type="radio" value="Ne" v-model="questionAnswer" />
              <span>NE</span>
            </label>
          </div>

          <div
            v-else
            class="alert-box error mt-3 d-flex align-items-center justify-content-center"
          >
            Uzávěrka pro odpovědi již vypršela.
          </div>
        </div>

        <div class="joker-card mt-4">
          <div
            class="form-check form-switch d-flex align-items-center justify-content-between p-0 w-100"
          >
            <label
              class="form-check-label text-white fw-bold d-flex align-items-center"
              for="jokerSwitch"
            >
              <svg
                class="icon-md me-2 joker-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polygon
                  points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"
                ></polygon>
              </svg>
              POUŽÍT JOKERA (2x BODY)
              <div class="small text-muted fw-normal mt-1 ms-4 ps-2">
                {{
                  jokerUsed
                    ? "Jokera jste již využili v jiném kole."
                    : "Máte k dispozici."
                }}
              </div>
            </label>
            <input
              class="form-check-input ms-3 joker-switch"
              type="checkbox"
              id="jokerSwitch"
              v-model="useJoker"
              :disabled="jokerUsed"
            />
          </div>
        </div>

        <button
          type="submit"
          class="btn-red-glow w-100 mt-5"
          :disabled="isSubmitting || (isAllMatchesStarted && isDeadlinePassed)"
        >
          {{ isSubmitting ? "UKLÁDÁM..." : "ODESLAT TIPY" }}
        </button>

        <transition name="fade">
          <div v-if="errorMessage" class="alert-box error mt-3 text-center">
            ⚠️ {{ errorMessage }}
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="successMessage"
            class="alert-box success mt-3 text-center d-flex align-items-center justify-content-center"
          >
            ✅ {{ successMessage }}
          </div>
        </transition>
      </div>

      <div v-else class="text-center py-5 text-muted">
        <p>Žádné zápasy k dispozici pro toto kolo.</p>
      </div>
    </form>
  </div>
</template>

<script>
import { db, auth } from "../auth";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  getDoc,
} from "firebase/firestore";

export default {
  name: "TipForm",
  props: {
    roundNumber: Number,
  },
  data() {
    return {
      matches: [],
      tips: {},
      useJoker: false,
      successMessage: "",
      errorMessage: "",
      loading: true,
      isSubmitting: false,
      jokerUsed: false,
      userId: auth.currentUser ? auth.currentUser.uid : null,

      // Proměnné pro otázku
      question: "",
      questionAnswer: "",
      deadline: null,
    };
  },
  computed: {
    isDeadlinePassed() {
      if (!this.deadline) return false;
      const d = this.deadline.toDate
        ? this.deadline.toDate()
        : new Date(this.deadline);
      return new Date() > d;
    },
    isAllMatchesStarted() {
      return this.matches.every((m) => this.isMatchStarted(m.matchDate));
    },
  },
  watch: {
    roundNumber: {
      immediate: true,
      handler(newRound) {
        if (newRound) {
          this.fetchMatches();
          this.fetchJokerStatus();
        }
      },
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleString("cs-CZ", {
        day: "numeric",
        month: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    isMatchStarted(matchDate) {
      if (!matchDate) return true;
      const d = matchDate.toDate ? matchDate.toDate() : new Date(matchDate);
      return new Date() > d;
    },

    async fetchQuestion() {
      if (!this.roundNumber) return;

      this.question = "";
      this.deadline = null;

      try {
        const docRef = doc(db, "questions", String(this.roundNumber));
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          this.question = data.question;
          this.deadline = data.deadline;
        } else {
          const q = query(
            collection(db, "questions"),
            where("roundNumber", "==", this.roundNumber)
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const data = querySnapshot.docs[0].data();
            this.question = data.question;
            this.deadline = data.deadline;
          }
        }
      } catch (error) {
        console.error("Chyba při načítání otázky:", error);
      }
    },

    async fetchMatches() {
      if (!this.roundNumber) return;
      this.loading = true;

      try {
        const matchesQuery = query(
          collection(db, "matches"),
          where("roundNumber", "==", this.roundNumber),
          orderBy("matchDate", "asc")
        );
        const querySnapshot = await getDocs(matchesQuery);

        if (querySnapshot.empty) {
          this.matches = [];
          this.loading = false;
          return;
        }

        this.matches = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
          };
        });

        this.matches.forEach((match) => {
          if (!this.tips[match.id]) {
            this.tips[match.id] = { homeScore: null, awayScore: null };
          }
        });

        await this.fetchUserTips();
        await this.fetchQuestion();
      } catch (error) {
        console.error("Chyba načítání:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchUserTips() {
      if (!this.userId || !this.roundNumber) return;
      try {
        const tipsQuery = query(
          collection(db, "tips"),
          where("user", "==", this.userId),
          where("roundNumber", "==", this.roundNumber)
        );
        const querySnapshot = await getDocs(tipsQuery);

        if (!querySnapshot.empty) {
          const data = querySnapshot.docs[0].data();
          const userTips = data.tips || {};

          this.matches.forEach((match) => {
            if (userTips[match.id]) {
              this.tips[match.id] = {
                homeScore: userTips[match.id].homeScore,
                awayScore: userTips[match.id].awayScore,
              };
            }
          });

          if (data.questionAnswer) {
            this.questionAnswer = data.questionAnswer;
          }
          if (data.useJoker) this.useJoker = true;
        }
      } catch (error) {
        console.error("Chyba tipů:", error);
      }
    },

    async fetchJokerStatus() {
      if (!this.userId) return;
      try {
        const userRef = doc(db, "users", this.userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          this.jokerUsed = userDoc.data().jokerUsed || false;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async submitTips() {
      if (this.isSubmitting) return;

      this.successMessage = "";
      this.errorMessage = "";

      let missingTip = false;

      this.matches.forEach((match) => {
        if (!this.isMatchStarted(match.matchDate)) {
          const t = this.tips[match.id];
          if (
            !t ||
            t.homeScore === null ||
            t.homeScore === "" ||
            t.awayScore === null ||
            t.awayScore === ""
          ) {
            missingTip = true;
          }
        }
      });

      if (missingTip) {
        this.errorMessage = "Musíte vyplnit tipy pro všechny otevřené zápasy!";
        return;
      }

      this.isSubmitting = true;

      try {
        const tipsCollection = collection(db, "tips");
        const q = query(
          tipsCollection,
          where("user", "==", this.userId),
          where("roundNumber", "==", this.roundNumber)
        );
        const querySnapshot = await getDocs(q);

        let tipDocRef;
        let existingData = {};

        if (!querySnapshot.empty) {
          tipDocRef = doc(db, "tips", querySnapshot.docs[0].id);
          existingData = querySnapshot.docs[0].data().tips || {};
        } else {
          const fixedDocId = `round_${this.roundNumber}_${this.userId}`;
          tipDocRef = doc(db, "tips", fixedDocId);
        }

        let tipsToSave = { ...existingData };

        this.matches.forEach((match) => {
          if (!this.isMatchStarted(match.matchDate)) {
            const t = this.tips[match.id];
            tipsToSave[match.id] = {
              homeScore: Number(t.homeScore),
              awayScore: Number(t.awayScore),
            };
          }
        });

        let answerToSave = this.questionAnswer;

        const payload = {
          user: this.userId,
          roundNumber: this.roundNumber,
          tips: tipsToSave,
          useJoker: this.useJoker,
          ...(!this.isDeadlinePassed && { questionAnswer: answerToSave }),
          submittedAt: new Date(),
        };

        await setDoc(tipDocRef, payload, { merge: true });

        // ==========================================================
        // OPRAVA PRO SKRÝVÁNÍ JOKERA V TABULCE
        // ==========================================================
        if (this.useJoker && !this.jokerUsed) {
          const userRef = doc(db, "users", this.userId);
          await updateDoc(userRef, {
            jokerUsed: true,
            jokerUsedRound: this.roundNumber,
          });
          this.jokerUsed = true;
        }

        this.successMessage = "Tipy byly úspěšně uloženy!";
        this.$emit("tips-submitted");
        await this.fetchUserTips();

        setTimeout(() => (this.successMessage = ""), 3000);
      } catch (error) {
        console.error("Chyba ukládání:", error);
        this.errorMessage = "Chyba při ukládání dat.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.tip-form-wrapper {
  max-width: 800px;
  margin: 0 auto;
  font-family: "Roboto", sans-serif;
  color: #fff;
}
.icon-xs {
  width: 14px;
  height: 14px;
}
.icon-sm {
  width: 18px;
  height: 18px;
}
.icon-md {
  width: 24px;
  height: 24px;
}
.bonus-icon {
  stroke: #dc3545;
}
.joker-icon {
  stroke: #ffc107;
  fill: rgba(255, 193, 7, 0.2);
}
.match-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  margin-bottom: 20px;
  padding: 20px;
  transition: border-color 0.3s;
}
.match-card:hover {
  border-color: #555;
}
.match-card.match-locked {
  opacity: 0.6;
  border-color: #222;
  background: #0d0d0d;
}
.match-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 0.8rem;
  font-weight: bold;
}
.date-badge {
  background: #222;
  color: #aaa;
  padding: 4px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}
.status-badge {
  padding: 4px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}
.status-badge.locked {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}
.status-badge.active {
  color: #28a745;
  background: rgba(40, 167, 69, 0.1);
}
.match-body {
  display: grid;
  grid-template-columns: 1fr 120px 1fr;
  align-items: center;
  gap: 10px;
}
.team-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.team-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  margin-bottom: 8px;
}
.team-name {
  font-weight: bold;
  font-size: 0.9rem;
  line-height: 1.2;
}
.score-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.score-input {
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 1.4rem;
  font-weight: bold;
  background: #000;
  border: 2px solid #444;
  color: #fff;
  border-radius: 8px;
}
.score-input:focus {
  outline: none;
  border-color: #dc3545;
}
.score-input:disabled {
  background: #1a1a1a;
  color: #666;
  border-color: #222;
}
.vs-divider {
  font-weight: bold;
  color: #666;
}
.bonus-card {
  border: 1px solid #dc3545;
  background: rgba(220, 53, 69, 0.05);
  border-radius: 12px;
  padding: 20px;
}
.bonus-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.bonus-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #dc3545;
  margin: 0;
}
.deadline-info {
  font-size: 0.8rem;
  color: #aaa;
}
.question-text {
  font-style: italic;
  font-size: 1.1rem;
}
.joker-card {
  background: linear-gradient(90deg, #333 0%, #111 100%);
  border: 1px solid #444;
  border-radius: 12px;
  padding: 15px 20px;
}
.joker-switch {
  width: 50px;
  height: 25px;
  cursor: pointer;
}
.joker-switch:checked {
  background-color: #dc3545;
  border-color: #dc3545;
}
.radio-group {
  display: flex;
  gap: 15px;
}
.custom-radio {
  flex: 1;
  background: #222;
  border: 1px solid #444;
  padding: 10px;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
}
.custom-radio input {
  display: none;
}
.custom-radio.selected {
  background: #dc3545;
  border-color: #dc3545;
  color: white;
}
.btn-red-glow {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  padding: 15px;
  border: none;
  border-radius: 30px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 1.1rem;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
}
.btn-red-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.6);
}
.btn-red-glow:disabled {
  background: #333;
  color: #666;
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}
.animate-fade-in {
  animation: fadeIn 0.5s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.alert-box {
  padding: 15px;
  border-radius: 8px;
  font-weight: bold;
}
.alert-box.success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid #28a745;
}
.alert-box.error {
  background: rgba(220, 53, 69, 0.1);
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
  text-align: center;
}
@media (max-width: 576px) {
  .match-body {
    grid-template-columns: 1fr 100px 1fr;
  }
  .score-input {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
}
</style>
